import { useGetRequirementFlagSettingsQuery } from '@/services/requirementFlagSettings';
import { BaseSpacing, BorderColor, BorderSize, Box, Button, Icon, toast } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useContext } from 'react';

import { FlagStatus, Flag as FlagType, RequirementFlagSettingsDocument, RequirementFlagSettingsType } from 'types';

import { FlagActionsPopover } from './FlagActionsPopover';
import { QueueFlagsContext } from './QueueFlagsContext';

export interface FlagProps {
  dataKey: string;
  hasFlags?: boolean;
  fileName?: string;
  baseCategory?: string;
  type?: string;
  flagType?: RequirementFlagSettingsType;
  padding?: BaseSpacing;
  borderColor?: BorderColor;
  borderWidth?: BorderSize | string;
  as?: 'button';
}

export const Flag = ({
  dataKey,
  hasFlags,
  fileName,
  baseCategory,
  type,
  flagType,
  padding,
  borderColor,
  borderWidth,
  as,
}: FlagProps) => {
  const { flagDrivenPackageReview } = useFlags();
  const {
    showModal,
    getFlag,
    resolveFlag,
    isLoading,
    setLoading,
    getFlagsByFlagType,
    showEditModal,
    showResolveModal,
    shouldHide,
  } = useContext(QueueFlagsContext);
  const currentFlag = getFlag(dataKey, fileName, false);
  const currentFlags = flagType ? getFlagsByFlagType(flagType, false, dataKey, fileName) || [] : [];
  const allFlags = [...(currentFlags ? currentFlags : []), ...(currentFlag ? [currentFlag] : [])];
  const { data: requirementFlagSettings } = useGetRequirementFlagSettingsQuery(
    { type: flagType },
    { skip: !flagType },
  );
  const flagsOnDisplay = hasFlags
    ? [...(flagDrivenPackageReview ? allFlags : currentFlag ? [currentFlag] : [])]
    : [];
  const setting: RequirementFlagSettingsDocument | undefined = requirementFlagSettings?.requirementFlagSettings?.[0];

  const handleFlagClick = useCallback(async () => {
    if (currentFlag) {
      if (!isLoading) {
        if (flagDrivenPackageReview) {
          showModal(dataKey, flagType, fileName, baseCategory, type);
        } else {
          setLoading(true);
          try {
            await resolveFlag(dataKey, FlagStatus.closed, fileName);
          } catch (e) {
            toast.error('Something went wrong. Please try again.');
            console.error({ e });
          }
          setLoading(false);
        }
      }
    } else {
      showModal(dataKey, flagType, fileName, baseCategory, type);
    }
  }, [
    currentFlag,
    isLoading,
    flagDrivenPackageReview,
    showModal,
    dataKey,
    flagType,
    fileName,
    baseCategory,
    type,
    setLoading,
    resolveFlag,
  ]);

  const handleAddNewFlag = () => {
    showModal(dataKey, flagType, fileName, baseCategory, type);
  };

  const handleEditFlag = (flag: FlagType) => {
    const flagIsEditable = Boolean(flag?.id && flag?.flagType);
    if (flagIsEditable) {
      showEditModal(flag);
    } else {
      showResolveModal(flag?.dataKey ?? '', fileName, flag?.id?.toString());
    }
  };

  if (as === 'button') {
    if (flagDrivenPackageReview && hasFlags) {
      return (
        <FlagActionsPopover
          flags={flagsOnDisplay}
          dataKey={dataKey}
          flagType={flagType}
          fileName={fileName}
          baseCategory={baseCategory}
          type={type}
          setting={setting}
          onAddFlag={handleAddNewFlag}
          onEditFlag={handleEditFlag}
          shouldHide={shouldHide}
        >
          <Button iconPrefix="flag" variant="tertiary" size="md" tone="neutral" />
        </FlagActionsPopover>
      );
    }

    return (
      <Button iconPrefix="flag-outline" variant="tertiary" size="md" tone="neutral" onClick={handleFlagClick} />
    );
  }

  return (
    <Box
      onClick={handleFlagClick}
      cursor={isLoading ? '' : 'pointer'}
      padding={padding}
      borderColor={borderColor}
      borderWidth={{ tablet: borderWidth }}
    >
      <Icon
        size="lg"
        name={hasFlags && flagDrivenPackageReview ? 'flag' : 'flag-outline'}
        color={isLoading ? 'disabled' : 'body-primary'}
      />
    </Box>
  );
};
